import { getRouteApi } from "@tanstack/react-router";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import { fetchAuthenticated, fetchAuthenticatedData } from "@/queries/shared";
import type { ExtractRequest } from "@/models/extract";

const STATE_ESIID_PATH = "0-";

export const stateLevelViewTypes = [
  { Value: "State Enrollment", Text: "Enrollment" },
] as const;

const stateLevelExtractTypes = new Set<string>(
  stateLevelViewTypes.map((type) => type.Value)
);

export const stateLevelDataTypes = ["ODS", "Certified"] as const;
export type StateLevelDataType = (typeof stateLevelDataTypes)[number];

export type CreateStateLevelExtractRequest = {
  ExtractType: string;
  ExtractFileName: string;
  ResultsEmailDestination: string;
  StateEnrollmentParameters: {
    ExtractDataType: StateLevelDataType | "";
    EnrollmentMonth: string;
  };
};

type RawStateLevelExtract = {
  extractRequestId: number;
  esiid: string;
  extractType: string;
  extractStatus: string;
  requestorUsername: string;
  fileName: string;
  filePath: string;
  fileSizeBytes: number;
  requestDate: string;
};

const toExtractRequest = (raw: RawStateLevelExtract): ExtractRequest => ({
  ExtractRequestId: raw.extractRequestId,
  Esiid: raw.esiid,
  ExtractType: raw.extractType,
  ExtractStatus: raw.extractStatus,
  RequestorUsername: raw.requestorUsername,
  FileName: raw.fileName,
  FilePath: raw.filePath,
  FileSizeBytes: raw.fileSizeBytes,
  RequestDate: raw.requestDate,
});

const rootRoute = getRouteApi("__root__");

export function useStateLevelExtracts(
  refetchInterval: number | false = false
): UseQueryResult<ExtractRequest[]> {
  const globalState = rootRoute.useLoaderData();
  return useQuery({
    queryKey: ["state-level-extracts"],
    queryFn: () =>
      fetchAuthenticatedData<RawStateLevelExtract[]>(
        globalState,
        `json/state/extracts/${STATE_ESIID_PATH}`
      ).then((extracts) =>
        extracts
          .filter((extract) => stateLevelExtractTypes.has(extract.extractType))
          .map((extract) => toExtractRequest(extract))
      ),
    refetchInterval,
  });
}

export function useCreateStateLevelExtract() {
  const globalState = rootRoute.useLoaderData();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateStateLevelExtractRequest) =>
      fetchAuthenticated(
        globalState,
        `/json/state/odata/extracts?esiidPath=${STATE_ESIID_PATH}`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ ...request, IncludeHeaderRow: true }),
        }
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["state-level-extracts"] }),
  });
}
